import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Apple, Play, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1660557989725-f511e9fa6267?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const EASE = [0.16, 1, 0.3, 1];

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: EASE, delay },
});

const StoreButton = ({ testid, icon: Icon, top, bottom, primary }) => (
  <button
    data-testid={testid}
    onClick={() => toast("Launching soon", { description: "The StayFlow app is on its way to the stores." })}
    className={`group inline-flex items-center gap-3 rounded-2xl px-5 py-3 transition-transform duration-200 hover:-translate-y-1 ${
      primary
        ? "bg-ink text-white shadow-[0_10px_30px_rgba(20,20,20,0.18)]"
        : "bg-white text-ink border border-black/10 shadow-sm hover:shadow-md"
    }`}
  >
    <Icon className={`w-6 h-6 ${primary ? "text-white" : "text-ink"}`} aria-hidden="true" />
    <span className="text-left leading-tight">
      <span className="block text-[0.65rem] uppercase tracking-widest opacity-70">{top}</span>
      <span className="block font-heading font-semibold text-base">{bottom}</span>
    </span>
  </button>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden" data-testid="hero">
      <div aria-hidden="true" className="absolute -top-24 -right-24 w-[38rem] h-[38rem] rounded-full bg-brand-soft blur-3xl opacity-60" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div {...reveal(0)} className="inline-flex items-center gap-2 rounded-full bg-white border border-black/5 pl-1.5 pr-4 py-1.5 shadow-sm">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand text-white text-[0.6rem] font-bold">SF</span>
            <span className="overline text-ink-soft">The Hospitality OS</span>
          </motion.div>

          <h1 className="mt-7 font-heading font-extrabold tracking-tight text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.06]">
            <span className="block pb-[0.06em] overflow-hidden">
              <motion.span {...reveal(0.15)} className="block">Run your entire</motion.span>
            </span>
            <span className="block pb-[0.06em] overflow-hidden">
              <motion.span {...reveal(0.27)} className="block">property from <span className="text-brand">one</span></motion.span>
            </span>
            <span className="block pb-[0.06em] overflow-hidden">
              <motion.span {...reveal(0.39)} className="block">calm workspace.</motion.span>
            </span>
          </h1>

          <motion.p {...reveal(0.5)} className="mt-8 max-w-xl text-lg text-ink-soft leading-relaxed">
            StayFlow replaces scattered WhatsApp threads and Google Forms with a single
            mobile-first system for rooms, tables, orders and invoices — with a built-in
            kitchen POS, AI smart booking and 100% offline capability.
          </motion.p>

          <motion.div {...reveal(0.6)} id="download" className="mt-10 flex flex-wrap items-center gap-4 scroll-mt-28">
            <StoreButton testid="hero-download-apple" icon={Apple} top="Download on the" bottom="App Store" primary />
            <StoreButton testid="hero-download-google" icon={Play} top="Get it on" bottom="Google Play" />
          </motion.div>

          <motion.p {...reveal(0.7)} className="mt-6 text-sm text-ink-faint font-mono">
            Coming soon to iOS & Android · www.stayflowapp.in
          </motion.p>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
            className="relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-[0_30px_80px_rgba(20,20,20,0.18)] border border-black/5">
              <motion.img
                src={HERO_IMG}
                alt="A warm, designed hospitality lobby"
                style={{ y: imgY, scale: imgScale }}
                className="w-full h-full object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="absolute -left-4 md:-left-10 bottom-8 bg-white rounded-2xl shadow-xl border border-black/5 px-5 py-4"
            >
              <p className="text-3xl font-heading font-extrabold text-ink">100%</p>
              <p className="text-xs text-ink-soft mt-0.5">works fully offline</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 flex items-center gap-2 text-ink-faint">
        <ArrowDown className="w-4 h-4 animate-bounce" aria-hidden="true" />
        <span className="text-xs uppercase tracking-widest">Scroll to explore</span>
      </div>
    </section>
  );
};

export default Hero;
